import type { Category, Profile, Work } from './types.ts'

/**
 * Odvodí kategorii díla z faktů, která o něm máme.
 *
 * Kategorie se neukládá na dílo záměrně: liší se škola od školy, ale rok vydání
 * a původ ne. Jedno obohacení tak sedí na libovolný profil kritérií a rozhodnutí
 * je dohledatelné — u sporného díla je vidět, z jakého roku a původu vypadlo.
 *
 * Vrací null, když dílu nesedne žádná kategorie. Volající to nesmí ignorovat:
 * dílo mimo všechny kategorie by se nezapočítalo a chyba by vyplavala u maturity.
 */
export function deriveCategoryId(work: Work, profile: Profile): string | null {
  const byOrder = [...profile.categories].sort((a, b) => a.order - b.order)

  if (work.categoryOverride) {
    // přebití platí jen na kategorii, kterou profil zná — jinak se propadne k odvození
    const hit = byOrder.find((c) => c.id === work.categoryOverride)
    if (hit) return hit.id
  }

  return byOrder.find((c) => matches(work, c))?.id ?? null
}

function matches(work: Work, category: Category): boolean {
  const d = category.derive
  if (!d) return true // kategorie bez predikátu je záchytná

  if (d.origin && d.origin !== 'any' && d.origin !== work.origin) return false

  const needsYear = d.yearFrom !== undefined || d.yearTo !== undefined
  if (needsYear) {
    if (work.firstPublished === null) return false
    if (d.yearFrom !== undefined && work.firstPublished < d.yearFrom) return false
    if (d.yearTo !== undefined && work.firstPublished > d.yearTo) return false
  }

  return true
}

/** Kategorie → díla. Používá panel pravidel i report obohacení. */
export function groupByCategory(works: Work[], profile: Profile): Map<string | null, Work[]> {
  const out = new Map<string | null, Work[]>()
  for (const w of works) {
    const key = deriveCategoryId(w, profile)
    const bucket = out.get(key)
    if (bucket) bucket.push(w)
    else out.set(key, [w])
  }
  return out
}
